import { CheckSquareOutlined, CloseSquareOutlined, PlusOutlined, WarningTwoTone } from "@ant-design/icons";
import { Button, Space, TableColumnsType, theme, Tooltip, Typography } from "antd";
import type { ColumnFilterItem } from "antd/es/table/interface";
import GoldenNominalBadge from "components/atoms/GoldenNominalBadge";
import SlideMenuGuide, { useSlideMenuGuide } from "components/atoms/SlideMenuGuide";
import DataTable from "components/molecules/DataTable";
import ProhibitedArea from "components/molecules/ProhibitedArea";
import { useAppSelector } from "hooks";
import moment from "moment";
import React from "react";
import { isDatasetGolden } from "common/goldenNominal";
import CypressTestIds from "../../../cypress/CypressTestIds";
import DataSetListActionButtons from "./DataSetListActionButtons";

const { useToken } = theme;

interface IDataSetListProps {
    dataSetList?: IDataSet[];
    allowCreateNew?: boolean;
    parentSchema: string;
    onCreateNewDataSet: () => void;
    onViewDataSet: (id: string) => void;
    onEditDataSet: (id: string) => void;
    onViewDataSetHistory: (id: string) => void;
    onDeleteDataSet: (s: IDataSet) => void;
    onIndexDataSet: (s: IDataSet) => void;
    onExportToFileClick?: (s: IDataSet) => void;
    onExportToSystemConnectionClick?: (s: IDataSet) => void;
    onPageSelect: (page: number, pageSize: number) => void;
    emptyText?: React.ReactNode;
    permission: IPermissionState;
    exportPermission?: IPermissionState;
    // Shown beside "Create a new data set" (e.g. the schema's review queue button).
    headerExtra?: React.ReactNode;
}

const DataSetList = ({
    dataSetList = [],
    allowCreateNew = false,
    parentSchema,
    onCreateNewDataSet,
    onEditDataSet,
    onDeleteDataSet,
    onViewDataSet,
    onViewDataSetHistory,
    onIndexDataSet,
    onExportToFileClick,
    onExportToSystemConnectionClick,
    onPageSelect,
    emptyText,
    permission,
    exportPermission,
    headerExtra,
}: IDataSetListProps) => {
    const { token } = useToken();
    const goldenBySchema = useAppSelector((s) => s.goldenNominal?.goldenBySchema);
    const [selectedRowKeys, setSelectedRowKeys] = React.useState<React.Key[]>([]);
    const [showMenuGuide, setShowMenuGuide] = React.useState<boolean>(false);
    const { isReady, guidePostion } = useSlideMenuGuide("ant-table-header", "ant-table-row");

    React.useEffect(() => {
        setShowMenuGuide(isReady);
    }, [isReady]);

    const allNames: ColumnFilterItem[] = React.useMemo(() => {
        const states: ColumnFilterItem[] = [];
        dataSetList.forEach((a) => {
            if (!states.some((s) => s.value === a.dataSetName)) {
                states.push({
                    text: a.dataSetName,
                    value: a.dataSetName,
                });
            }
        });
        return states;
    }, [dataSetList]);

    const onClickRow = (record: IDataSet) => {
        return {
            onClick: () => {
                setSelectedRowKeys([record.dataSetId]); // Supports single selection only
                onViewDataSet(record.dataSetId);
            },
        };
    };

    const columns: TableColumnsType<IDataSet> = React.useMemo(
        () => [
            {
                fixed: "left",
                width: 40,
                className: "overflow-visible !p-1",
                render: (_v, dataSet) => (
                    <DataSetListActionButtons
                        dataSet={dataSet}
                        selected={selectedRowKeys.some((a) => a === dataSet.dataSetId)}
                        runningJobsCount={dataSet.runningJobsCount}
                        permission={permission}
                        exportPermission={exportPermission}
                        onEditDataSet={onEditDataSet}
                        onViewDataSet={onViewDataSet}
                        onDeleteDataSet={onDeleteDataSet}
                        onIndexDataSet={onIndexDataSet}
                        onViewDataSetHistory={onViewDataSetHistory}
                        onExportToFileClick={onExportToFileClick}
                        onExportToSystemConnectionClick={onExportToSystemConnectionClick}
                    />
                ),
            },
            {
                width: 300,
                dataIndex: "dataSetName",
                title: "DataSet",
                ellipsis: {
                    showTitle: false,
                },
                render: (value, dataSet) => (
                    <span className="inline-flex items-center gap-2">
                        <Tooltip placement="topLeft" title={value}>
                            <span className="truncate">{value}</span>
                        </Tooltip>
                        {isDatasetGolden(goldenBySchema, dataSet.schemaId, dataSet.dataSetId, dataSet.isGoldenNominal) && <GoldenNominalBadge />}
                    </span>
                ),
                sorter: (a, b) => a.dataSetName.length - b.dataSetName.length,
                filters: allNames,
                filterMode: "menu",
                filterSearch: true,
                onFilter: (value, record) => record.dataSetName?.startsWith(value as string),
            },
            {
                width: 300,
                dataIndex: "schemaName",
                title: "Schema",
                ellipsis: {
                    showTitle: false,
                },
                render: (_v, _d, _i) => (
                    <Tooltip placement="topLeft" title={parentSchema}>
                        {parentSchema}
                    </Tooltip>
                ),
            },
            {
                width: 100,
                dataIndex: "recordCount",
                title: "Records",
                ellipsis: {
                    showTitle: false,
                },
                render: (value, dataSet, _i) => (
                    <>
                        {!!dataSet.erroredRecordsCount && (
                            <Tooltip placement="topLeft" title={`Contains ${dataSet.erroredRecordsCount} error row(s)`}>
                                <WarningTwoTone twoToneColor="#FF0000" className="mr-2" />
                            </Tooltip>
                        )}
                        <Tooltip placement="topLeft" title={value}>
                            {value}
                        </Tooltip>
                    </>
                ),
            },
            {
                width: 80,
                dataIndex: "shared",
                title: "Shared",
                ellipsis: {
                    showTitle: false,
                },
                render: (value, _d, _i) => {
                    return value ? (
                        <CheckSquareOutlined style={{ fontSize: "16px", color: token.colorSuccessText }} />
                    ) : (
                        <CloseSquareOutlined style={{ fontSize: "16px", color: token.colorTextDisabled }} />
                    );
                },
            },
            {
                width: 220,
                dataIndex: "updatedDate",
                title: "Last Updated",
                ellipsis: {
                    showTitle: false,
                },
                render: (value, _d, _i) => {
                    let dateValue = moment.utc(value).toDate().toLocaleString();
                    return (
                        <Tooltip placement="topLeft" title={dateValue}>
                            {dateValue}
                        </Tooltip>
                    );
                },
            },
            {
                width: 300,
                dataIndex: "createdByEmail",
                title: "Created By",
                ellipsis: {
                    showTitle: false,
                },
                render: (value, _d, _i) => (
                    <Tooltip placement="topLeft" title={value}>
                        {value}
                    </Tooltip>
                ),
            },
        ],
        [
            allNames,
            exportPermission,
            goldenBySchema,
            onDeleteDataSet,
            onEditDataSet,
            onExportToFileClick,
            onExportToSystemConnectionClick,
            onIndexDataSet,
            onViewDataSet,
            onViewDataSetHistory,
            parentSchema,
            permission,
            selectedRowKeys,
            token.colorSuccessText,
            token.colorTextDisabled,
        ],
    );

    if (!permission) {
        return <ProhibitedArea></ProhibitedArea>;
    }

    return (
        <DataTable<IDataSet>
            dataTestId={CypressTestIds.DATASET_LIST_TABLE}
            header={
                <>
                    <Typography.Title level={3} className="!mb-0">
                        DataSet
                    </Typography.Title>
                    <Space>
                        {headerExtra}
                        <Button
                            disabled={!permission.canAdd || !allowCreateNew}
                            type="primary"
                            onClick={onCreateNewDataSet}
                            data-testid={CypressTestIds.DATASET_LIST_CREATE_NEW_DATASET_BUTTON}
                        >
                            <PlusOutlined /> Create a new data set
                        </Button>
                    </Space>
                </>
            }
            resizable
            hideSelectionColumn
            tableProps={{
                dataSource: dataSetList,
                columns: columns,
                locale: emptyText ? { emptyText } : undefined,
                onRow: onClickRow,
                onChange: (pagination, _filters, _sorter, extra) =>
                    extra.action === "paginate" && onPageSelect && onPageSelect(pagination.current ?? 1, pagination.pageSize ?? 100),
                rowSelection: {
                    type: "radio",
                    selectedRowKeys,
                    onChange: (selected) => setSelectedRowKeys(selected),
                },
                rowKey: "dataSetId",
                rowClassName: (record: IDataSet) =>
                    isDatasetGolden(goldenBySchema, record.schemaId, record.dataSetId, record.isGoldenNominal) ? "cursor-pointer bg-amber-50" : "cursor-pointer",
                pagination: {
                    defaultPageSize: 50,
                    pageSize: 50,
                },
            }}
        >
            {dataSetList.length > 0 && <SlideMenuGuide open={showMenuGuide} position={guidePostion} onClose={() => setShowMenuGuide(false)} />}
        </DataTable>
    );
};

export default DataSetList;
